import {
  compileVegaLiteFloatSeriesQuery,
  decodeVegaLiteFloatSeriesOptions,
} from './vega-lite-float-series-compiler';
import type { VegaLiteFloatSeriesOptionsV2 } from './vega-lite-float-series-types';
import { cloneJsonObject, isJsonObject, type JsonObject, utf8ByteLength } from './vega-lite-json';
import { assertVegaLiteJsonTree, validateVegaLiteJsonTree } from './vega-lite-json-tree';
import validationMetadata from './vega-lite-validation-metadata.json';
import {
  VEGA_LITE_EVALUATION_LIMITS,
  type VegaLiteEvaluationLimits,
  type VegaLiteFloatSeriesLimits,
} from './vega-lite-widget-limits';

export const VEGA_LITE_OPTIONS_VERSION = 1;

export const VEGA_LITE_WIDGET_LIMITS = {
  maxRecordMetrics: VEGA_LITE_EVALUATION_LIMITS.maxRecordMetrics,
  maxGroupByFields: VEGA_LITE_EVALUATION_LIMITS.maxGroupByFields,
  maxSpecBytes: 100 * 1024,
  maxSerializedOptionsBytes: 128 * 1024,
} as const;

/** Overrides of the deployment limits used by options validation, for example in tests. */
export type VegaLiteWidgetOptionsLimits = {
  evaluation?: Pick<VegaLiteEvaluationLimits, 'maxRecordMetrics' | 'maxGroupByFields'>;
  floatSeries?: VegaLiteFloatSeriesLimits;
};

export const VEGA_LITE_DATASET_NAME = validationMetadata.datasetName;

/**
 * Provenance columns the adapter always writes onto prepared rows. Field aliases must not collide
 * with them, otherwise run/record identity would be silently overwritten by a selected metric.
 */
export const VEGA_LITE_RESERVED_FIELD_NAMES: readonly string[] =
  validationMetadata.evaluation.reservedFieldNames;

const CHART_WIDE_OUTPUT_FIELD_NAMES = validationMetadata.evaluation.chartWideOutputFieldNames;
const LONG_OUTPUT_FIELD_NAMES = validationMetadata.evaluation.longOutputFieldNames;
const CHART_LONG_OUTPUT_FIELD_NAMES = ['x', ...LONG_OUTPUT_FIELD_NAMES] as const;
const NO_FIXED_OUTPUT_FIELD_NAMES: readonly string[] = [];

const FIELD_ALIAS_PATTERN = /^[A-Za-z][A-Za-z0-9_]*$/;

export type EvaluationFieldType = 'FLOAT64' | 'STRING';

export type EvaluationField = {
  name: string;
  type: EvaluationFieldType;
  as: string;
};

export type VegaLiteChartQuery = {
  kind: 'chart';
  xMetricName: string;
  yMetricName: string;
};

export type VegaLiteRecordsQuery = {
  kind: 'records';
  metrics: EvaluationField[];
};

export type VegaLiteQuery = VegaLiteChartQuery | VegaLiteRecordsQuery;

export type VegaLiteDataFormat = 'wide' | 'long';

export type VegaLiteWidgetOptionsV1 = {
  version: typeof VEGA_LITE_OPTIONS_VERSION;
  query: VegaLiteQuery;
  dataFormat: VegaLiteDataFormat;
  groupBy: EvaluationField[];
  spec: JsonObject;
};

export type VegaLiteWidgetOptions = VegaLiteWidgetOptionsV1 | VegaLiteFloatSeriesOptionsV2;
export type VegaLiteSupportedOptions = VegaLiteWidgetOptions;

/**
 * Structural diagnostics shared by the editor, DSL and request validation. Paths are relative to
 * the options object; `/` denotes the whole object. Metadata lets consumers adapt presentation
 * without parsing messages or repeating validation rules. Names are unique in encounter order;
 * alias paths identify the first contributing field in metrics-then-groups order.
 */
export type VegaLiteWidgetOptionsIssue = {
  path: string;
  message: string;
} & (
  | {
      code:
        | 'invalidFloatSeries'
        | 'missingXAxis'
        | 'missingYAxis'
        | 'duplicateAxes'
        | 'missingRecordMetrics'
        | 'missingRecordMetricName'
        | 'missingGroupName';
    }
  | {
      code:
        | 'axisGroupOverlap'
        | 'duplicateRecordMetrics'
        | 'duplicateGroups'
        | 'invalidAliases'
        | 'reservedAliases'
        | 'duplicateAliases'
        | 'groupOutputAliasCollision';
      names: string[];
    }
  | {
      code: 'tooManyRecordMetrics' | 'tooManyGroups' | 'specTooLarge' | 'optionsTooLarge';
      limit: number;
    }
);

/**
 * Options persisted by a newer client. They stay opaque and read-only instead of being coerced into
 * the supported shape, so an unrelated save cannot drop them.
 */
export type UnsupportedVegaLiteWidgetOptions = {
  version: number;
  payload: JsonObject;
};

export type VegaLiteWidgetOptionsDecodeResult =
  | { status: 'supported'; options: VegaLiteWidgetOptions }
  | { status: 'unsupportedVersion'; options: UnsupportedVegaLiteWidgetOptions }
  | { status: 'invalid'; errors: string[] };

export function createDefaultVegaLiteWidgetOptions(): VegaLiteWidgetOptionsV1 {
  return {
    version: VEGA_LITE_OPTIONS_VERSION,
    query: { kind: 'chart', xMetricName: '', yMetricName: '' },
    dataFormat: 'wide',
    groupBy: [],
    spec: {},
  };
}

export function isEvaluationFieldType(value: unknown): value is EvaluationFieldType {
  return value === 'FLOAT64' || value === 'STRING';
}

export function isEvaluationField(value: unknown): value is EvaluationField {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }

  const candidate: Record<string, unknown> = { ...value };

  return (
    typeof candidate.name === 'string' &&
    isEvaluationFieldType(candidate.type) &&
    typeof candidate.as === 'string'
  );
}

export function isVegaLiteDataFormat(value: unknown): value is VegaLiteDataFormat {
  return value === 'wide' || value === 'long';
}

function isEvaluationFieldArray(value: unknown): value is EvaluationField[] {
  return Array.isArray(value) && value.every(isEvaluationField);
}

function isVegaLiteQuery(value: unknown): value is VegaLiteQuery {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }

  const candidate: Record<string, unknown> = { ...value };

  if (candidate.kind === 'chart') {
    return typeof candidate.xMetricName === 'string' && typeof candidate.yMetricName === 'string';
  }

  if (candidate.kind === 'records') {
    return isEvaluationFieldArray(candidate.metrics);
  }

  return false;
}

export function isVegaLiteWidgetOptions(value: unknown): value is VegaLiteWidgetOptions {
  if (!isJsonObject(value)) {
    return false;
  }

  const candidate: Record<string, unknown> = { ...value };

  if (candidate.version === 2) {
    return decodeVegaLiteFloatSeriesOptions(candidate).status === 'valid';
  }

  return (
    candidate.version === VEGA_LITE_OPTIONS_VERSION &&
    isVegaLiteQuery(candidate.query) &&
    isVegaLiteDataFormat(candidate.dataFormat) &&
    isEvaluationFieldArray(candidate.groupBy) &&
    isJsonObject(candidate.spec)
  );
}

function normalizeEvaluationField(field: EvaluationField): EvaluationField {
  return { name: field.name, type: field.type, as: field.as };
}

function normalizeQuery(query: VegaLiteQuery): VegaLiteQuery {
  if (query.kind === 'chart') {
    return {
      kind: 'chart',
      xMetricName: query.xMetricName,
      yMetricName: query.yMetricName,
    };
  }

  return {
    kind: 'records',
    metrics: query.metrics.map(normalizeEvaluationField),
  };
}

/**
 * Produces a canonical options object: known keys only, in a stable order, with copied arrays.
 * Both the API and the DSL converters rely on this so a round trip cannot smuggle extra keys.
 */
export function normalizeVegaLiteWidgetOptions(
  options: VegaLiteWidgetOptionsV1,
): VegaLiteWidgetOptionsV1;
export function normalizeVegaLiteWidgetOptions(
  options: VegaLiteFloatSeriesOptionsV2,
): VegaLiteFloatSeriesOptionsV2;
export function normalizeVegaLiteWidgetOptions(
  options: VegaLiteWidgetOptions,
): VegaLiteWidgetOptions;

export function normalizeVegaLiteWidgetOptions(
  options: VegaLiteWidgetOptions,
): VegaLiteWidgetOptions {
  assertVegaLiteJsonTree(options);

  if (options.version === 2) {
    const decoded = decodeVegaLiteFloatSeriesOptions(options);

    if (decoded.status === 'invalid') {
      throw new Error(decoded.issues.map((issue) => `${issue.path}: ${issue.message}`).join(' '));
    }

    return decoded.options;
  }

  return {
    version: VEGA_LITE_OPTIONS_VERSION,
    query: normalizeQuery(options.query),
    dataFormat: options.dataFormat,
    groupBy: options.groupBy.map(normalizeEvaluationField),
    spec: cloneJsonObject(options.spec),
  };
}

export function decodeVegaLiteWidgetOptions(value: unknown): VegaLiteWidgetOptionsDecodeResult {
  const validation = validateVegaLiteJsonTree(value);

  if (validation.status === 'invalid') {
    return {
      status: 'invalid',
      errors: [`${validation.issue.path}: ${validation.issue.message}`],
    };
  }

  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return { status: 'invalid', errors: ['Vega-Lite widget options must be an object.'] };
  }

  const candidate: Record<string, unknown> = { ...value };

  if (typeof candidate.version !== 'number' || !Number.isInteger(candidate.version)) {
    return {
      status: 'invalid',
      errors: ['Vega-Lite widget options require an integer "version".'],
    };
  }

  if (candidate.version === 2) {
    const decoded = decodeVegaLiteFloatSeriesOptions(candidate);
    return decoded.status === 'valid'
      ? { status: 'supported', options: decoded.options }
      : {
          status: 'invalid',
          errors: decoded.issues.map((issue) => `${issue.path}: ${issue.message}`),
        };
  }

  if (candidate.version !== VEGA_LITE_OPTIONS_VERSION) {
    if (!isJsonObject(candidate)) {
      return {
        status: 'invalid',
        errors: [
          `Unsupported Vega-Lite widget options version ${candidate.version} is not JSON-serializable.`,
        ],
      };
    }

    return {
      status: 'unsupportedVersion',
      options: { version: candidate.version, payload: candidate },
    };
  }

  if (!isVegaLiteWidgetOptions(candidate)) {
    return {
      status: 'invalid',
      errors: ['Vega-Lite widget options do not match the version 1 contract.'],
    };
  }

  return { status: 'supported', options: normalizeVegaLiteWidgetOptions(candidate) };
}

export function getVegaLiteSelectedFields(
  options: Pick<VegaLiteWidgetOptionsV1, 'groupBy' | 'query'>,
): EvaluationField[] {
  const metricFields = options.query.kind === 'records' ? options.query.metrics : [];
  return [...metricFields, ...options.groupBy];
}

export function getVegaLiteGroupByFixedOutputFieldNames(
  options: Pick<VegaLiteWidgetOptionsV1, 'dataFormat' | 'query'>,
): readonly string[] {
  if (options.dataFormat === 'wide') {
    return options.query.kind === 'chart'
      ? CHART_WIDE_OUTPUT_FIELD_NAMES
      : NO_FIXED_OUTPUT_FIELD_NAMES;
  }

  return options.query.kind === 'chart' ? CHART_LONG_OUTPUT_FIELD_NAMES : LONG_OUTPUT_FIELD_NAMES;
}

function validateFieldAliases(
  options: VegaLiteWidgetOptionsV1,
  issues: VegaLiteWidgetOptionsIssue[],
) {
  const metricCount = options.query.kind === 'records' ? options.query.metrics.length : 0;
  const aliases = getVegaLiteSelectedFields(options).map((field, index) => ({
    name: field.as,
    path: index < metricCount ? `/query/metrics/${index}/as` : `/groupBy/${index - metricCount}/as`,
  }));
  const reservedNames: readonly string[] = VEGA_LITE_RESERVED_FIELD_NAMES;
  const invalidAliases = aliases.filter(({ name }) => !FIELD_ALIAS_PATTERN.test(name));

  if (invalidAliases.length > 0) {
    const names = Array.from(new Set(invalidAliases.map(({ name }) => name)));
    issues.push({
      code: 'invalidAliases',
      path: invalidAliases[0].path,
      names,
      message: `Field aliases must start with a letter and use only letters, digits or underscores: "${names.join(
        ', ',
      )}".`,
    });
  }

  const reservedAliases = aliases.filter(
    ({ name }) => reservedNames.includes(name) || name === VEGA_LITE_DATASET_NAME,
  );

  if (reservedAliases.length > 0) {
    const names = Array.from(new Set(reservedAliases.map(({ name }) => name)));
    issues.push({
      code: 'reservedAliases',
      path: reservedAliases[0].path,
      names,
      message: `Reserved field aliases: "${names.join(', ')}".`,
    });
  }

  const aliasCounts = new Map<string, number>();
  aliases.forEach(({ name }) => aliasCounts.set(name, (aliasCounts.get(name) ?? 0) + 1));
  const duplicateAliases = aliases.filter(({ name }) => (aliasCounts.get(name) ?? 0) > 1);

  if (duplicateAliases.length > 0) {
    const names = Array.from(new Set(duplicateAliases.map(({ name }) => name)));
    issues.push({
      code: 'duplicateAliases',
      path: duplicateAliases[0].path,
      names,
      message: `Duplicate field aliases: "${names.join(', ')}".`,
    });
  }
}

function validateGroupByOutputFieldAliases(
  options: VegaLiteWidgetOptionsV1,
  issues: VegaLiteWidgetOptionsIssue[],
) {
  const fixedOutputFieldNames = getVegaLiteGroupByFixedOutputFieldNames(options);
  const conflictingAliases = options.groupBy
    .map((field, index) => ({ name: field.as, path: `/groupBy/${index}/as` }))
    .filter(({ name }) => fixedOutputFieldNames.includes(name));

  if (conflictingAliases.length > 0) {
    const names = Array.from(new Set(conflictingAliases.map(({ name }) => name)));
    issues.push({
      code: 'groupOutputAliasCollision',
      path: conflictingAliases[0].path,
      names,
      message: `Reserved field aliases: "${names.join(', ')}".`,
    });
  }
}

function validateFieldReferences(
  fields: EvaluationField[],
  collection: 'metrics' | 'groupBy',
  issues: VegaLiteWidgetOptionsIssue[],
) {
  const path = collection === 'metrics' ? '/query/metrics' : '/groupBy';

  if (fields.some((field) => field.name.length === 0)) {
    issues.push({
      code: collection === 'metrics' ? 'missingRecordMetricName' : 'missingGroupName',
      path,
      message: `Every ${
        collection === 'metrics' ? 'selected metric' : 'grouping dimension'
      } requires a metric name.`,
    });
  }

  const referenceCounts = new Map<string, { name: string; count: number }>();
  fields.forEach((field) => {
    const reference = JSON.stringify([field.name, field.type]);
    referenceCounts.set(reference, {
      name: field.name,
      count: (referenceCounts.get(reference)?.count ?? 0) + 1,
    });
  });
  const names = Array.from(
    new Set(
      Array.from(referenceCounts.values())
        .filter(({ count }) => count > 1)
        .map(({ name }) => name),
    ),
  );

  if (names.length > 0) {
    issues.push({
      code: collection === 'metrics' ? 'duplicateRecordMetrics' : 'duplicateGroups',
      path,
      names,
      message: `Duplicate ${
        collection === 'metrics' ? 'selected metrics' : 'grouping dimensions'
      }: "${names.join(', ')}".`,
    });
  }
}

/**
 * Structural validation that does not need discovered metrics. Metric existence and type checks
 * belong to the discovery-aware validation performed by the configuration layer.
 */
export function getVegaLiteWidgetOptionsIssues(
  options: VegaLiteWidgetOptions,
  limits: VegaLiteWidgetOptionsLimits = {},
): VegaLiteWidgetOptionsIssue[] {
  if (options.version === 2) {
    const compiled = compileVegaLiteFloatSeriesQuery(options.query, limits.floatSeries);
    const issues: VegaLiteWidgetOptionsIssue[] =
      compiled.status === 'invalid'
        ? compiled.issues.map((issue) => ({ ...issue, code: 'invalidFloatSeries' }))
        : [];
    (
      [
        ['specTooLarge', '/spec', options.spec, VEGA_LITE_WIDGET_LIMITS.maxSpecBytes],
        ['optionsTooLarge', '/', options, VEGA_LITE_WIDGET_LIMITS.maxSerializedOptionsBytes],
      ] as const
    ).forEach(([code, path, value, limit]) => {
      if (utf8ByteLength(JSON.stringify(value)) > limit) {
        issues.push({
          code,
          path,
          limit,
          message: `The serialized value must not exceed ${limit} UTF-8 bytes.`,
        });
      }
    });
    return issues;
  }

  const issues: VegaLiteWidgetOptionsIssue[] = [];
  const { query, groupBy } = options;
  const { maxRecordMetrics, maxGroupByFields } = limits.evaluation ?? VEGA_LITE_WIDGET_LIMITS;

  if (query.kind === 'chart') {
    if (!query.xMetricName) {
      issues.push({
        code: 'missingXAxis',
        path: '/query/xMetricName',
        message: 'Select an X-axis metric.',
      });
    }

    if (!query.yMetricName) {
      issues.push({
        code: 'missingYAxis',
        path: '/query/yMetricName',
        message: 'Select a Y-axis metric.',
      });
    }

    if (query.xMetricName && query.yMetricName && query.xMetricName === query.yMetricName) {
      issues.push({
        code: 'duplicateAxes',
        path: '/query',
        message: 'X-axis and Y-axis metrics must be different.',
      });
    }

    const axisGroupNames = groupBy
      .map((field) => field.name)
      .filter((name) => name === query.xMetricName || name === query.yMetricName);

    if (axisGroupNames.length > 0) {
      const names = Array.from(new Set(axisGroupNames));
      issues.push({
        code: 'axisGroupOverlap',
        path: '/groupBy',
        names,
        message: `Grouping dimensions must not use selected axis metrics: "${names.join(', ')}".`,
      });
    }
  } else {
    if (query.metrics.length < 1) {
      issues.push({
        code: 'missingRecordMetrics',
        path: '/query/metrics',
        message: 'Select at least one metric.',
      });
    }

    if (query.metrics.length > maxRecordMetrics) {
      issues.push({
        code: 'tooManyRecordMetrics',
        path: '/query/metrics',
        limit: maxRecordMetrics,
        message: `Select no more than ${maxRecordMetrics} metrics.`,
      });
    }

    validateFieldReferences(query.metrics, 'metrics', issues);
  }

  if (groupBy.length > maxGroupByFields) {
    issues.push({
      code: 'tooManyGroups',
      path: '/groupBy',
      limit: maxGroupByFields,
      message: `Select no more than ${maxGroupByFields} grouping dimensions.`,
    });
  }

  validateFieldReferences(groupBy, 'groupBy', issues);
  validateFieldAliases(options, issues);
  validateGroupByOutputFieldAliases(options, issues);

  const serializedSpecLength = JSON.stringify(options.spec).length;

  if (serializedSpecLength > VEGA_LITE_WIDGET_LIMITS.maxSpecBytes) {
    issues.push({
      code: 'specTooLarge',
      path: '/spec',
      limit: VEGA_LITE_WIDGET_LIMITS.maxSpecBytes,
      message: `The Vega-Lite definition must not exceed ${VEGA_LITE_WIDGET_LIMITS.maxSpecBytes} bytes.`,
    });
  }

  if (JSON.stringify(options).length > VEGA_LITE_WIDGET_LIMITS.maxSerializedOptionsBytes) {
    issues.push({
      code: 'optionsTooLarge',
      path: '/',
      limit: VEGA_LITE_WIDGET_LIMITS.maxSerializedOptionsBytes,
      message: `Serialized options must not exceed ${VEGA_LITE_WIDGET_LIMITS.maxSerializedOptionsBytes} bytes.`,
    });
  }

  return issues;
}

export function validateVegaLiteWidgetOptions(
  options: VegaLiteWidgetOptions,
  limits?: VegaLiteWidgetOptionsLimits,
): string[] {
  const validation = validateVegaLiteJsonTree(options);

  if (validation.status === 'invalid') {
    return [`${validation.issue.path}: ${validation.issue.message}`];
  }

  return getVegaLiteWidgetOptionsIssues(options, limits).map((issue) => issue.message);
}

export function createEvaluationFieldAlias(prefix: 'm' | 'g', index: number): string {
  return `${prefix}${index}`;
}
